import { Alert, List, ListItem, Stack, Typography } from "@mui/material"
import { useMemo } from "react"
import { DialogBox } from "lib/@components/dialogBox"
import { Done } from "lib/@components/done"
import { If } from "lib/@components/switch"
import Iconify from "minimals-template/components/Iconify"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"

export function SendToReviewBlockersDialog({ schedules = [], scheduleCount = 0 }) {
    const summary = useMemo(summariseSchedules, [schedules])

    return (
        <DialogBox
            title={<Typography variant="h5">No Schedules Ready for Review</Typography>}
            Actions={Done}
            okCaption="Close"
            value={true}
            data-cy="send-to-review-blockers-dialog"
        >
            <Stack spacing={2}>
                <Alert
                    severity="warning"
                    icon={<Iconify icon="material-symbols:warning-rounded" />}
                    sx={{
                        alignItems: "flex-start",
                        "& .MuiAlert-icon": { display: "flex", alignItems: "center", mt: 0.25 },
                    }}
                >
                    <Stack spacing={1}>
                        <If truthy={scheduleCount === 0}>
                            <Typography variant="subtitle1">
                                Add at least one schedule to this work package before sending to review.
                            </Typography>
                        </If>
                        <If truthy={scheduleCount > 0}>
                            <Typography variant="subtitle1">
                                No schedules are in editing state. All schedules must be in editing state with a draft
                                to be submitted for review.
                            </Typography>
                            <List
                                dense
                                disablePadding
                                sx={{
                                    listStyleType: "disc",
                                    listStylePosition: "inside",
                                    pl: 1,
                                    "& .MuiListItem-root": { display: "list-item", py: 0 },
                                }}
                            >
                                <If truthy={summary.submittedCount > 0}>
                                    <ListItem>{summary.submittedCount} already submitted for review</ListItem>
                                </If>
                                <If truthy={summary.approvedCount > 0}>
                                    <ListItem>{summary.approvedCount} already approved</ListItem>
                                </If>
                                <If truthy={summary.rejectedCount > 0}>
                                    <ListItem>{summary.rejectedCount} rejected — needs author changes</ListItem>
                                </If>
                                <If truthy={summary.noDraftCount > 0}>
                                    <ListItem>{summary.noDraftCount} without a draft</ListItem>
                                </If>
                            </List>
                        </If>
                    </Stack>
                </Alert>
            </Stack>
        </DialogBox>
    )

    function summariseSchedules() {
        return schedules.reduce(
            (acc, schedule) => {
                if (!schedule?.isDraft) {
                    acc.noDraftCount += 1
                } else if (schedule.status === DraftStatus.SUBMITTED) {
                    acc.submittedCount += 1
                } else if (schedule.status === DraftStatus.APPROVED) {
                    acc.approvedCount += 1
                } else if (schedule.status === DraftStatus.REJECTED) {
                    acc.rejectedCount += 1
                }
                return acc
            },
            { submittedCount: 0, approvedCount: 0, rejectedCount: 0, noDraftCount: 0 }
        )
    }
}
